import React from 'react';
import { useHistory } from 'react-router-dom';
import { SliderModal } from '@components/molecules/SlideOver';
import { LowStockAlertCard } from '@components/organisms/LowStockAlertCard';
import {
  useLowStockAlerts,
  formatAlertNumber,
} from '@hooks/api/warehouse/useLowStockAlerts';
import { useLeadTimeAlertCounts } from '@hooks/api/warehouse';
import { Loading } from '@components/atoms/Loading';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

interface WarehouseAlertsSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with a material catalog id when the user wants to receive stock. */
  onOrder: (catalogId: string) => void;
}

/**
 * Dashboard slide-over that surfaces the same low-stock alert cards as the
 * Warehouse Alerts page, grouped by warehouse. Receiving stock is delegated to
 * the parent (which opens ReceiveStockModal) so the modal isn't trapped behind
 * this slide-over's z-index layer.
 */
export const WarehouseAlertsSlideOver: React.FC<
  WarehouseAlertsSlideOverProps
> = ({ isOpen, onClose, onOrder }) => {
  const history = useHistory();
  const { warehouseGroups, alertItems, isLoading } = useLowStockAlerts();
  const { data: leadTimeAlertCounts } = useLeadTimeAlertCounts();
  const leadTimeCount = leadTimeAlertCounts?.total || 0;

  const goToWarehouseAlerts = () => {
    onClose();
    history.push('/warehouse/alerts');
  };

  return (
    <SliderModal
      isOpen={isOpen}
      closeSlider={onClose}
      cancelText="Close"
      title={
        <span className="flex items-center gap-2">
          <WarningAmberIcon className="text-amber-500" sx={{ fontSize: 22 }} />
          Warehouse Alerts
        </span>
      }>
      <div className="space-y-6 pb-24">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <Loading size="xl" center />
          </div>
        ) : (
          <>
            {/* No low-stock alerts state */}
            {alertItems.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-10 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Inventory2Icon
                    className="text-green-600"
                    sx={{ fontSize: 28 }}
                  />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  All Stock Levels OK
                </h3>
                <p className="text-sm text-gray-400">
                  No items are currently below their reorder point.
                </p>
              </div>
            )}

            {/* Low stock grouped by warehouse (critical items first) */}
            {warehouseGroups.map((group) => (
              <div key={group.warehouseId} className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                  <WarehouseIcon
                    sx={{ fontSize: 16 }}
                    className="text-gray-400"
                  />
                  {group.name}
                  <span className="text-xs font-medium text-gray-400 normal-case">
                    ({group.items.length})
                  </span>
                </h2>
                {group.items.map((item) => (
                  <LowStockAlertCard
                    key={item.stockLevel.stockLevelId}
                    item={item}
                    onOrder={onOrder}
                    formatNumber={formatAlertNumber}
                  />
                ))}
              </div>
            ))}

            {/* Lead-time alerts live on the full Warehouse Alerts page; surface a
                link so the slide-over count and the full picture stay reconciled. */}
            {leadTimeCount > 0 && (
              <button
                type="button"
                onClick={goToWarehouseAlerts}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg py-3 transition-colors">
                <LocalShippingIcon sx={{ fontSize: 18 }} />
                View {formatAlertNumber(leadTimeCount)} lead-time alert
                {leadTimeCount === 1 ? '' : 's'} in Warehouse
              </button>
            )}
          </>
        )}
      </div>
    </SliderModal>
  );
};
